import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, ErrorState, Loading, Stat, Empty, PaymentBadge } from '../components/ui.jsx';
import { IconBuy, IconSell } from '../components/Icons.jsx';
import { useApi } from '../lib/hooks.js';
import { t } from '../lib/i18n.js';
import { formatDate, liters, money, moneyPair, percent, today } from '../lib/format.js';

export default function Dashboard() {
  const { data, error, loading, reload } = useApi('/api/dashboard', { today: today() });

  return (
    <>
      <PageHeader
        title={t.dashboard.title}
        subtitle={formatDate(today())}
        actions={
          <div className="btn-row no-print">
            <Link to="/buy/new" className="btn btn-sm">
              <IconBuy /> {t.nav.buy}
            </Link>
            <Link to="/sell/new" className="btn btn-sm btn-primary">
              <IconSell /> {t.nav.sell}
            </Link>
          </div>
        }
      />

      <div className="page">
        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && (
          <>
            <div className="stat-grid">
              <Stat
                label={t.dashboard.soldToday}
                value={liters(data.sales.today_liters)}
                hint={`${t.dashboard.saleCount(data.sales.today_count)} · ${moneyPair(
                  data.sales.today_iqd,
                  data.sales.today_usd,
                )}`}
                tone="accent"
              />
              <Stat
                label={t.dashboard.boughtToday}
                value={liters(data.purchases.today_liters)}
                hint={`${t.dashboard.loadCount(data.purchases.today_count)} · ${moneyPair(
                  data.purchases.today_iqd,
                  data.purchases.today_usd,
                )}`}
              />
              <Stat
                label={t.dashboard.customersOweUs}
                value={money(data.debts.customers_owe_iqd, 'IQD')}
                hint={`${money(data.debts.customers_owe_usd, 'USD')} · ${t.dashboard.accountCount(
                  data.debts.customers_in_debt,
                )}`}
                tone="success"
              />
              <Stat
                label={t.dashboard.weOweSuppliers}
                value={money(data.debts.we_owe_iqd, 'IQD')}
                hint={`${money(data.debts.we_owe_usd, 'USD')} · ${t.dashboard.accountCount(
                  data.debts.suppliers_owed,
                )}`}
                tone="danger"
              />
            </div>

            <Card
              title={t.dashboard.tanks}
              actions={
                <Link to="/tanks" className="btn btn-sm btn-ghost">
                  {t.dashboard.manage}
                </Link>
              }
            >
              {data.tanks.length === 0 ? (
                <Empty title={t.dashboard.noTanks} message={t.dashboard.noTanksHint} />
              ) : (
                <div className="divide">
                  {data.tanks.map((tank) => (
                    <TankGauge key={tank.id} tank={tank} />
                  ))}
                </div>
              )}
            </Card>

            <div className="stat-grid">
              <Stat
                label={t.dashboard.monthSold}
                value={liters(data.sales.month_liters)}
                hint={moneyPair(data.sales.month_iqd, data.sales.month_usd)}
              />
              <Stat
                label={t.dashboard.monthBought}
                value={liters(data.purchases.month_liters)}
                hint={moneyPair(data.purchases.month_iqd, data.purchases.month_usd)}
              />
              <Stat
                label={t.dashboard.monthExpenses}
                value={moneyPair(data.expenses.month_iqd, data.expenses.month_usd, {
                  zero: money(0, 'IQD'),
                })}
              />
              <ExchangeBalanceStat debts={data.debts} />
            </div>

            <Card
              title={t.dashboard.latestActivity}
              actions={
                <Link to="/sell" className="btn btn-sm btn-ghost">
                  {t.dashboard.allSales}
                </Link>
              }
            >
              {data.recent.length === 0 ? (
                <Empty title={t.dashboard.nothingYet} message={t.dashboard.nothingYetHint} />
              ) : (
                <div className="record-list">
                  {data.recent.map((item) => (
                    <Link
                      key={`${item.kind}-${item.id}`}
                      className="record"
                      to={item.kind === 'sale' ? `/sell/${item.id}/receipt` : `/buy/${item.id}/receipt`}
                    >
                      <div className="record-top">
                        <span className={`badge ${item.kind === 'sale' ? 'sell' : 'buy'}`}>
                          {item.kind === 'sale' ? t.dashboard.sale : t.dashboard.purchase}
                        </span>
                        <span className="title">{item.party_name}</span>
                        <span className="amount">{money(item.amount, item.currency)}</span>
                      </div>
                      <div className="record-meta">
                        <span>{item.ref_no}</span>
                        <span>{formatDate(item.entry_date)}</span>
                        <span>{liters(item.liters)}</span>
                        <PaymentBadge type={item.payment_type} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}

/**
 * Net position across every exchange company: what they hold for us minus
 * what we owe them, in each currency independently (no cross-currency netting).
 */
function ExchangeBalanceStat({ debts }) {
  const netIqd = Number(debts.exchange_held_iqd) - Number(debts.exchange_owed_iqd);
  const netUsd = Number(debts.exchange_held_usd) - Number(debts.exchange_owed_usd);
  return (
    <Stat
      label={t.dashboard.exchangeBalance}
      value={money(netIqd, 'IQD')}
      hint={money(netUsd, 'USD')}
      tone={netIqd < 0 || netUsd < 0 ? 'danger' : netIqd > 0 || netUsd > 0 ? 'success' : ''}
    />
  );
}

export function TankGauge({ tank }) {
  const fill = Number(tank.fill_percent ?? 0);
  const tone = fill <= 5 ? 'gauge-empty' : fill <= 20 ? 'gauge-low' : '';
  return (
    <div className="tank">
      <div className="tank-head">
        <span className="name">{tank.name}</span>
        <span className="pct">{percent(fill)}</span>
      </div>
      <div className={`gauge ${tone}`}>
        <span style={{ width: `${Math.max(0, Math.min(100, fill))}%` }} />
      </div>
      <div className="tank-foot">
        <span>{liters(tank.current_liters)}</span>
        <span>{t.tank.of(liters(tank.capacity_liters))}</span>
      </div>
    </div>
  );
}
